import { useNavigate, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from '../../lib/axios'
import { BarChart3, Users, GitBranch, Calendar, Settings, Play, Pause, RotateCcw, CheckCircle, Clock, RefreshCw, ChevronRight } from 'lucide-react'
import '../Pages.css'

const Tabs = ({ activeTab, campaignId, onStatusChange }) => {
  const navigate = useNavigate()
  const [campaign, setCampaign] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const tabs = [
    { name: 'Analytics', icon: BarChart3 },
    { name: 'Leads', icon: Users },
    { name: 'Sequence', icon: GitBranch },
    { name: 'Schedule', icon: Calendar },
    { name: 'Options', icon: Settings }
  ]

  useEffect(() => {
    fetchCampaignStatus()
  }, [campaignId])

  const fetchCampaignStatus = async () => {
    try {
      const res = await axios.get('email-campaign/get-all')
      const campaignData = res.data.find(c => c.id === campaignId)
      if (campaignData) {
        setCampaign(campaignData)
      }
    } catch (err) {
      console.error('Failed to fetch campaign status:', err)
    }
  }

  const handleStart = async () => {
    setActionLoading(true)
    try {
      await axios.post(`email-campaign/${campaignId}/start`)
      await fetchCampaignStatus()
      if (onStatusChange) onStatusChange('active')
    } catch (err) {
      const detail = err.response?.data?.detail
      if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
        alert('Cannot launch campaign:\n\n' + detail.errors.map(e => `• ${e}`).join('\n'))
      } else {
        alert(typeof detail === 'string' ? detail : 'Failed to start campaign')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handlePause = async () => {
    setActionLoading(true)
    try {
      await axios.post(`email-campaign/${campaignId}/pause`)
      await fetchCampaignStatus()
      if (onStatusChange) onStatusChange('paused')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to pause campaign')
    } finally {
      setActionLoading(false)
    }
  }

  const handleResume = async () => {
    setActionLoading(true)
    try {
      await axios.post(`email-campaign/${campaignId}/resume`)
      await fetchCampaignStatus()
      if (onStatusChange) onStatusChange('active')
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to resume campaign')
    } finally {
      setActionLoading(false)
    }
  }

  const handleTabChange = (tab) => {
    navigate(`/campaigns/${tab.toLowerCase()}/${campaignId}`)
  }

  const getStatusBadge = () => {
    if (!campaign) return null

    const statusConfig = {
      draft: { label: 'Draft', bg: 'rgba(100, 116, 139, 0.1)', color: '#64748b', pulse: false },
      active: { label: 'Active', bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', pulse: true },
      paused: { label: 'Paused', bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', pulse: false },
      completed: { label: 'Completed', bg: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', pulse: false }
    }

    const config = statusConfig[campaign.status] || statusConfig.draft

    return (
      <span className="campaign-status-badge" style={{ background: config.bg, color: config.color }}>
        <span className={`campaign-pulse-dot${config.pulse ? ' animate' : ''}`} />
        {config.label}
      </span>
    )
  }

  const getActionButtons = () => {
    if (!campaign) return null
    if (campaign.status !== 'active' && campaign.status !== 'paused') return null

    if (actionLoading) {
      return (
        <button className="btn btn-sm" disabled style={{ minWidth: '100px' }}>
          <RefreshCw size={14} className="animate-spin" />
        </button>
      )
    }

    return (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        {campaign.status === 'active' && (
          <button
            className="btn btn-sm"
            onClick={handlePause}
            style={{
              background: 'rgba(234, 179, 8, 0.1)',
              color: '#ca8a04',
              border: '1px solid rgba(234, 179, 8, 0.3)'
            }}
          >
            <Pause size={14} />
            <span>Pause Campaign</span>
          </button>
        )}
        {campaign.status === 'paused' && (
          <button
            className="btn btn-sm"
            onClick={handleResume}
            style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              border: 'none'
            }}
          >
            <RotateCcw size={14} />
            <span>Resume Campaign</span>
          </button>
        )}
      </div>
    )
  }

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {/* Breadcrumb */}
      <div className="campaign-breadcrumb">
        <Link to="/campaigns">Campaigns</Link>
        <ChevronRight size={14} className="separator" />
        <span className="current">{campaign?.name || 'Campaign'}</span>
      </div>

      {/* Campaign Header with Status + Actions */}
      {campaign && (
        <div className="campaign-header-row">
          <div className="campaign-header-left">
            <h2>{campaign.name}</h2>
            {getStatusBadge()}
          </div>
          {getActionButtons()}
        </div>
      )}

      {/* Pill Tabs */}
      <div className="campaign-pill-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.name}
              onClick={() => handleTabChange(tab.name)}
              className={`campaign-pill-tab${activeTab === tab.name ? ' active' : ''}`}
            >
              <Icon size={16} />
              {tab.name}
            </button>
          )
        })}
      </div>
    </div>
  )
}

Tabs.propTypes = {
  activeTab: PropTypes.string.isRequired,
  campaignId: PropTypes.string.isRequired,
  onStatusChange: PropTypes.func
}

export default Tabs
